import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Select from 'react-select';
import { groupAPI, tagAPI, categoryAPI, feeManagementAPI } from '../services/api';
import { useToast } from '../components/Toast';
import { useAuth } from '../context/AuthContext';

function GroupDetails() {
  const { groupId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const { showToast } = useToast();
  const [group, setGroup] = useState(null);
  const [leaderboard, setLeaderboard] = useState([]);
  const [quizzes, setQuizzes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('students'); // 'students', 'quizzes', 'leaderboard', 'tags', 'categories', or 'fees'
  const [availableTags, setAvailableTags] = useState([]);
  const [selectedTags, setSelectedTags] = useState([]);
  const [savingTags, setSavingTags] = useState(false);
  const [availableCategories, setAvailableCategories] = useState([]);
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [savingCategories, setSavingCategories] = useState(false);
  const [feeStatistics, setFeeStatistics] = useState(null);
  const [feeManagements, setFeeManagements] = useState([]);
  const [payments, setPayments] = useState([]);
  const [loadingFees, setLoadingFees] = useState(false);
  const [studentSearch, setStudentSearch] = useState('');

  useEffect(() => {
    if (user && user.role !== 'admin') {
      showToast('Access denied. Admin only.', 'error');
      navigate('/user');
    }
  }, [user, navigate, showToast]);

  useEffect(() => {
    fetchGroupDetails();
    fetchGroupLeaderboard();
    fetchGroupQuizzes();
    fetchTags();
    fetchCategories();
    fetchGroupFees();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupId]);

  useEffect(() => {
    if (group && group.tags) {
      setSelectedTags(group.tags.map(tag => ({ value: tag._id, label: tag.name })));
    }
    if (group && group.categories) {
      setSelectedCategories(group.categories.map(cat => ({ value: cat._id, label: cat.name })));
    }
  }, [group]);

  const fetchTags = async () => {
    try {
      const response = await tagAPI.getAllTags();
      const tags = response.data.tags || [];
      setAvailableTags(tags.map(tag => ({ value: tag._id, label: tag.name })));
    } catch (error) {
      console.error('Failed to fetch tags:', error);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await categoryAPI.getCategoryTree();
      const categories = response.data.categories || [];
      // Flatten categories for select options
      const flatCategories = flattenCategories(categories);
      setAvailableCategories(flatCategories.map(cat => ({ 
        value: cat._id, 
        label: cat.parentName ? `${cat.parentName} → ${cat.name}` : cat.name 
      })));
    } catch (error) {
      console.error('Failed to fetch categories:', error);
    }
  };

  const flattenCategories = (cats, parentName = null) => {
    let flat = [];
    cats.forEach(cat => {
      flat.push({ ...cat, parentName });
      if (cat.children && cat.children.length > 0) {
        flat = flat.concat(flattenCategories(cat.children, cat.name));
      }
    });
    return flat;
  };

  const fetchGroupDetails = async () => {
    try {
      const response = await groupAPI.getGroup(groupId);
      setGroup(response.data.group);
    } catch (error) {
      showToast(error.response?.data?.message || 'Failed to fetch group details', 'error');
      navigate('/groups');
    } finally {
      setLoading(false);
    }
  };

  const fetchGroupLeaderboard = async () => {
    try {
      const response = await groupAPI.getGroupLeaderboard(groupId);
      setLeaderboard(response.data.leaderboard);
    } catch (error) {
      console.error('Failed to fetch leaderboard:', error);
    }
  };

  const fetchGroupQuizzes = async () => {
    try {
      const response = await groupAPI.getGroupQuizzes(groupId);
      setQuizzes(response.data.quizzes || []);
    } catch (error) {
      console.error('Failed to fetch quizzes:', error);
    }
  };

  const fetchGroupFees = async () => {
    setLoadingFees(true);
    try {
      const response = await feeManagementAPI.getGroupFees(groupId);
      setFeeStatistics(response.data.statistics);
      setFeeManagements(response.data.feeManagements || []);
      setPayments(response.data.payments || []);
    } catch (error) {
      console.error('Failed to fetch group fees:', error);
      // Don't show error toast as fees might not be set up for this group
    } finally {
      setLoadingFees(false);
    }
  };

  const getMedalEmoji = (rank) => {
    if (rank === 1) return '🥇';
    if (rank === 2) return '🥈';
    if (rank === 3) return '🥉';
    return `#${rank}`;
  };

  const handleRemoveStudent = async (studentId, studentName) => {
    if (!window.confirm(`Are you sure you want to remove ${studentName} from this group?`)) {
      return;
    }

    try {
      await groupAPI.removeStudent(groupId, studentId);
      showToast('Student removed successfully', 'success');
      fetchGroupDetails(); // Refresh group data
    } catch (error) {
      showToast(error.response?.data?.message || 'Failed to remove student', 'error');
    }
  };

  const handleSaveTags = async () => {
    try {
      setSavingTags(true);
      const tagIds = selectedTags.map(tag => tag.value);
      await groupAPI.updateGroupTags(groupId, tagIds);
      showToast('Group tags updated successfully', 'success');
      fetchGroupDetails(); // Refresh group data
    } catch (error) {
      showToast(error.response?.data?.message || 'Failed to update group tags', 'error');
    } finally {
      setSavingTags(false);
    }
  };

  const handleSaveCategories = async () => {
    try {
      setSavingCategories(true);
      const categoryIds = selectedCategories.map(cat => cat.value);
      await groupAPI.updateGroupCategories(groupId, categoryIds);
      showToast('Group categories updated successfully', 'success');
      fetchGroupDetails(); // Refresh group data
    } catch (error) {
      showToast(error.response?.data?.message || 'Failed to update group categories', 'error');
    } finally {
      setSavingCategories(false);
    }
  };

  if (loading) {
    return (
      <div style={{ padding: '20px', textAlign: 'center' }}>
        <p style={{ color: '#fff' }}>Loading group details...</p>
      </div>
    );
  }

  if (!group) {
    return null;
  }

  return (
    <div className="container">
      <div className="card">
        <div style={{ display: 'flex', gap: '12px', marginBottom: '20px', flexWrap: 'wrap' }}>
          <button
            className="btn btn-secondary"
            onClick={() => navigate('/groups')}
            style={{ fontSize: '12px' }}
          >
            ← Back to Groups
          </button>
          <button
            className="btn"
            onClick={() => navigate(`/groups/${groupId}/lecture-assignments`)}
            style={{ 
              fontSize: '12px',
              background: '#10b981',
              color: '#ffffff',
              border: 'none'
            }}
          >
            📚 View Lecture Assignments
          </button>
        </div>

        <div style={{ marginBottom: '20px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
            <h2 style={{ color: '#667eea', margin: 0, fontSize: '22px' }}>
              👥 {group.name}
            </h2>
            {group.isBatch && (
              <span style={{
                padding: '4px 12px',
                backgroundColor: '#f0f4ff',
                color: '#667eea',
                border: '1px solid #c7d2fe',
                borderRadius: '12px',
                fontSize: '11px',
                fontWeight: '600',
                letterSpacing: '0.5px',
                textTransform: 'uppercase'
              }}>
                🎓 Batch
              </span>
            )}
          </div>
          <p style={{ color: '#718096', marginBottom: '12px', fontSize: '13px' }}>
            {group.description || `${group.students.length} student${group.students.length !== 1 ? 's' : ''}`}
          </p>
        </div>

        {/* Tabs */}
        <div style={{ 
          marginBottom: '20px', 
          borderBottom: '2px solid #e2e8f0',
          display: 'flex',
          gap: '4px'
        }}>
          <button
            onClick={() => setActiveTab('students')}
            style={{
              padding: '10px 20px',
              fontSize: '13px',
              fontWeight: '600',
              background: 'none',
              border: 'none',
              borderBottom: activeTab === 'students' ? '3px solid #667eea' : '3px solid transparent',
              color: activeTab === 'students' ? '#667eea' : '#718096',
              cursor: 'pointer',
              transition: 'all 0.2s'
            }}
          >
            👥 Students ({group.students.length})
          </button>
          <button
            onClick={() => setActiveTab('quizzes')}
            style={{
              padding: '10px 20px',
              fontSize: '13px',
              fontWeight: '600',
              background: 'none',
              border: 'none',
              borderBottom: activeTab === 'quizzes' ? '3px solid #667eea' : '3px solid transparent',
              color: activeTab === 'quizzes' ? '#667eea' : '#718096',
              cursor: 'pointer',
              transition: 'all 0.2s'
            }}
          >
            📋 Quizzes ({quizzes.length})
          </button>
          <button
            onClick={() => setActiveTab('leaderboard')}
            style={{
              padding: '10px 20px',
              fontSize: '13px',
              fontWeight: '600',
              background: 'none',
              border: 'none',
              borderBottom: activeTab === 'leaderboard' ? '3px solid #667eea' : '3px solid transparent',
              color: activeTab === 'leaderboard' ? '#667eea' : '#718096',
              cursor: 'pointer',
              transition: 'all 0.2s'
            }}
          >
            🏆 Leaderboard
          </button>
          <button
            onClick={() => setActiveTab('tags')}
            style={{
              padding: '10px 20px',
              fontSize: '13px',
              fontWeight: '600',
              background: 'none',
              border: 'none',
              borderBottom: activeTab === 'tags' ? '3px solid #667eea' : '3px solid transparent',
              color: activeTab === 'tags' ? '#667eea' : '#718096',
              cursor: 'pointer',
              transition: 'all 0.2s'
            }}
          >
            🏷️ Tags ({group.tags?.length || 0})
          </button>
          <button
            onClick={() => setActiveTab('categories')}
            style={{
              padding: '10px 20px',
              fontSize: '13px',
              fontWeight: '600',
              background: 'none',
              border: 'none',
              borderBottom: activeTab === 'categories' ? '3px solid #667eea' : '3px solid transparent',
              color: activeTab === 'categories' ? '#667eea' : '#718096',
              cursor: 'pointer',
              transition: 'all 0.2s'
            }}
          >
            📂 Categories ({group.categories?.length || 0})
          </button>
          <button
            onClick={() => setActiveTab('fees')}
            style={{
              padding: '10px 20px',
              fontSize: '13px',
              fontWeight: '600',
              background: 'none',
              border: 'none',
              borderBottom: activeTab === 'fees' ? '3px solid #667eea' : '3px solid transparent',
              color: activeTab === 'fees' ? '#667eea' : '#718096',
              cursor: 'pointer',
              transition: 'all 0.2s'
            }}
          >
            💰 Fees
          </button>
        </div>

        {activeTab === 'students' && (
          <div>
          <div style={{ marginBottom: '16px' }}>
            <input
              type="text"
              placeholder="Search by name or email..."
              value={studentSearch}
              onChange={(e) => setStudentSearch(e.target.value)}
              style={{
                width: '100%',
                padding: '10px 12px',
                fontSize: '14px',
                border: '1px solid #e2e8f0',
                borderRadius: '6px',
                backgroundColor: 'white',
                color: '#2d3748',
                boxSizing: 'border-box',
                transition: 'border-color 0.2s'
              }}
              onFocus={(e) => e.target.style.borderColor = '#667eea'}
              onBlur={(e) => e.target.style.borderColor = '#e2e8f0'}
            />
          </div>
          {group.students.length === 0 ? (
            <div style={{
              textAlign: 'center',
              padding: '40px',
              color: '#718096',
              backgroundColor: '#f7fafc',
              borderRadius: '8px'
            }}>
              <p style={{ fontSize: '18px', marginBottom: '8px' }}>👥</p>
              <p>No students in this group yet.</p>
            </div>
          ) : (
            <div style={{ display: 'grid', gap: '12px' }}>
              {[...group.students]
                .sort((a, b) => a.name.localeCompare(b.name))
                .filter(student => 
                  student.name.toLowerCase().includes(studentSearch.toLowerCase()) ||
                  student.email.toLowerCase().includes(studentSearch.toLowerCase())
                )
                .map((student, index) => (
                <div
                  key={student._id}
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '16px',
                    backgroundColor: 'white',
                    borderRadius: '8px',
                    border: '1px solid #e2e8f0'
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <div
                      style={{
                        width: '40px',
                        height: '40px',
                        borderRadius: '50%',
                        backgroundColor: '#667eea',
                        color: 'white',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '16px',
                        fontWeight: '600'
                      }}
                    >
                      {student.name.charAt(0).toUpperCase()}
                    </div>
                    <div>
                      <p 
                        onClick={() => navigate(`/students/${student._id}`)}
                        style={{ margin: '0 0 4px 0', fontSize: '15px', fontWeight: '600', color: '#667eea', textTransform: 'uppercase', cursor: 'pointer', transition: 'all 0.2s' }}
                        onMouseEnter={(e) => e.currentTarget.style.textDecoration = 'underline'}
                        onMouseLeave={(e) => e.currentTarget.style.textDecoration = 'none'}
                      >
                        {student.name}
                      </p>
                      <p style={{ margin: 0, fontSize: '13px', color: '#718096' }}>
                        {student.email}
                      </p>
                    </div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <div style={{ fontSize: '13px', color: '#a0aec0' }}>
                      #{index + 1}
                    </div>
                    <button
                      onClick={() => handleRemoveStudent(student._id, student.name)}
                      style={{
                        padding: '6px 12px',
                        fontSize: '13px',
                        backgroundColor: '#fff',
                        color: '#e53e3e',
                        border: '1px solid #feb2b2',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        transition: 'all 0.2s',
                        fontWeight: '500'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = '#e53e3e';
                        e.currentTarget.style.color = 'white';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = '#fff';
                        e.currentTarget.style.color = '#e53e3e';
                      }}
                    >
                      ✕ Remove
                    </button>
                  </div>
                </div>
              ))}
              {[...group.students].filter(student => 
                student.name.toLowerCase().includes(studentSearch.toLowerCase()) ||
                student.email.toLowerCase().includes(studentSearch.toLowerCase())
              ).length === 0 && (
                <div style={{
                  textAlign: 'center',
                  padding: '40px',
                  color: '#718096',
                  backgroundColor: '#f7fafc',
                  borderRadius: '8px'
                }}>
                  <p style={{ fontSize: '18px', marginBottom: '8px' }}>🔍</p>
                  <p>No students match your search.</p>
                </div>
              )}
            </div>
          )}
          </div>
        )}

        {activeTab === 'quizzes' && (
          <div>
          {quizzes.length === 0 ? (
            <div style={{
              textAlign: 'center',
              padding: '40px',
              color: '#718096',
              backgroundColor: '#f7fafc',
              borderRadius: '8px'
            }}>
              <p style={{ fontSize: '18px', marginBottom: '8px' }}>📋</p>
              <p>No quizzes assigned to this group yet.</p>
            </div>
          ) : (
            <div style={{ display: 'grid', gap: '12px' }}>
              {quizzes.map((quiz) => (
                <div
                  key={quiz._id}
                  style={{
                    padding: '16px',
                    backgroundColor: 'white',
                    borderRadius: '8px',
                    border: '1px solid #e2e8f0',
                    transition: 'all 0.2s'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor = '#667eea';
                    e.currentTarget.style.boxShadow = '0 4px 12px rgba(102, 126, 234, 0.1)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = '#e2e8f0';
                    e.currentTarget.style.boxShadow = 'none';
                  }}
                >
                  <h4 style={{ margin: '0 0 8px 0', fontSize: '16px', fontWeight: '600', color: '#2d3748' }}>
                    {quiz.title}
                  </h4>
                  {quiz.description && (
                    <p style={{ margin: '0 0 12px 0', fontSize: '14px', color: '#718096' }}>
                      {quiz.description}
                    </p>
                  )}
                  <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', fontSize: '13px', marginBottom: '12px' }}>
                    <span style={{ 
                      padding: '4px 10px', 
                      background: '#eef2ff', 
                      color: '#667eea',
                      borderRadius: '6px',
                      fontWeight: '600'
                    }}>
                      📋 {quiz.quizCode}
                    </span>
                    {quiz.quizType && (
                      <span style={{
                        padding: '4px 10px',
                        background: '#fef3c7',
                        color: '#d69e2e',
                        borderRadius: '6px',
                        fontWeight: '600'
                      }}>
                        📝 {quiz.quizType}
                      </span>
                    )}
                    {quiz.difficulty && (
                      <span style={{ 
                        padding: '4px 10px', 
                        background: quiz.difficulty === 'Easy' ? '#c6f6d5' : quiz.difficulty === 'Medium' ? '#fef3c7' : '#fecaca',
                        color: quiz.difficulty === 'Easy' ? '#22543d' : quiz.difficulty === 'Medium' ? '#78350f' : '#7f1d1d',
                        borderRadius: '6px',
                        fontWeight: '600'
                      }}>
                        {quiz.difficulty === 'Easy' ? '🟢' : quiz.difficulty === 'Medium' ? '🟡' : '🔴'} {quiz.difficulty}
                      </span>
                    )}
                    <span style={{ color: '#4a5568' }}>📝 {quiz.questionsCount} questions</span>
                    <span style={{ color: '#4a5568' }}>⏱️ {quiz.duration} mins</span>
                    <span style={{ 
                      padding: '4px 10px', 
                      backgroundColor: quiz.submissionCount > 0 ? '#c6f6d5' : '#fed7d7',
                      color: quiz.submissionCount > 0 ? '#2f855a' : '#c53030',
                      borderRadius: '12px',
                      fontWeight: '600'
                    }}>
                      👥 {quiz.submissionCount || 0} submission{quiz.submissionCount !== 1 ? 's' : ''}
                    </span>
                  </div>
                  <button
                    onClick={() => navigate('/admin', { state: { selectedQuiz: quiz } })}
                    style={{
                      padding: '8px 16px',
                      backgroundColor: '#667eea',
                      color: 'white',
                      border: 'none',
                      borderRadius: '6px',
                      fontSize: '13px',
                      fontWeight: '600',
                      cursor: 'pointer',
                      transition: 'background-color 0.2s'
                    }}
                    onMouseEnter={(e) => e.target.style.backgroundColor = '#5a67d8'}
                    onMouseLeave={(e) => e.target.style.backgroundColor = '#667eea'}
                  >
                    📊 View Submissions
                  </button>
                </div>
              ))}
            </div>
          )}
          </div>
        )}

        {activeTab === 'leaderboard' && (
          <div>
          {leaderboard.length === 0 ? (
            <div style={{
              textAlign: 'center',
              padding: '40px',
              color: '#718096',
              backgroundColor: '#f7fafc',
              borderRadius: '8px'
            }}>
              <p style={{ fontSize: '18px', marginBottom: '8px' }}>📊</p>
              <p>No quiz submissions yet from this group.</p>
            </div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ backgroundColor: '#f7fafc', borderBottom: '2px solid #e2e8f0' }}>
                    <th style={{ 
                      padding: '12px', 
                      textAlign: 'left', 
                      fontSize: '13px', 
                      fontWeight: '700', 
                      color: '#2d3748' 
                    }}>
                      Rank
                    </th>
                    <th style={{ 
                      padding: '12px', 
                      textAlign: 'left', 
                      fontSize: '13px', 
                      fontWeight: '700', 
                      color: '#2d3748' 
                    }}>
                      Name
                    </th>
                    <th style={{ 
                      padding: '12px', 
                      textAlign: 'center', 
                      fontSize: '13px', 
                      fontWeight: '700', 
                      color: '#2d3748' 
                    }}>
                      Quizzes Taken
                    </th>
                    <th style={{ 
                      padding: '12px', 
                      textAlign: 'center', 
                      fontSize: '13px', 
                      fontWeight: '700', 
                      color: '#2d3748' 
                    }}>
                      Avg Score
                    </th>
                    <th style={{ 
                      padding: '12px', 
                      textAlign: 'center', 
                      fontSize: '13px', 
                      fontWeight: '700', 
                      color: '#2d3748' 
                    }}>
                      Avg %
                    </th>
                    <th style={{ 
                      padding: '12px', 
                      textAlign: 'center', 
                      fontSize: '13px', 
                      fontWeight: '700', 
                      color: '#2d3748' 
                    }}>
                      Total Score
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {leaderboard.map((entry) => {
                    const isTopThree = entry.rank <= 3;

                    return (
                      <tr
                        key={entry.userId}
                        style={{
                          backgroundColor: isTopThree ? '#f0fff4' : 'white',
                          borderBottom: '1px solid #e2e8f0',
                          transition: 'background-color 0.2s'
                        }}
                      >
                        <td style={{ 
                          padding: '16px 12px', 
                          fontSize: '16px',
                          fontWeight: '700',
                          color: isTopThree ? '#38a169' : '#4a5568'
                        }}>
                          {getMedalEmoji(entry.rank)}
                        </td>
                        <td style={{ padding: '16px 12px' }}>
                          <div>
                            <div style={{ 
                              fontSize: '14px', 
                              fontWeight: '600', 
                              color: '#2d3748',
                              marginBottom: '2px'
                            }}>
                              {entry.name}
                            </div>
                            <div style={{ fontSize: '12px', color: '#a0aec0' }}>
                              {entry.email}
                            </div>
                          </div>
                        </td>
                        <td style={{ 
                          padding: '16px 12px', 
                          textAlign: 'center',
                          fontSize: '14px',
                          color: '#4a5568'
                        }}>
                          {entry.quizzesTaken}
                        </td>
                        <td style={{ 
                          padding: '16px 12px', 
                          textAlign: 'center',
                          fontSize: '14px',
                          fontWeight: '600',
                          color: '#2d3748'
                        }}>
                          {entry.averageScore}
                        </td>
                        <td style={{ 
                          padding: '16px 12px', 
                          textAlign: 'center' 
                        }}>
                          <span style={{
                            display: 'inline-block',
                            padding: '4px 12px',
                            borderRadius: '12px',
                            fontSize: '13px',
                            fontWeight: '600',
                            backgroundColor: entry.averagePercentage >= 80 ? '#c6f6d5' : 
                                           entry.averagePercentage >= 60 ? '#fef5e7' : '#fed7d7',
                            color: entry.averagePercentage >= 80 ? '#22543d' : 
                                   entry.averagePercentage >= 60 ? '#744210' : '#742a2a'
                          }}>
                            {entry.averagePercentage}%
                          </span>
                        </td>
                        <td style={{ 
                          padding: '16px 12px', 
                          textAlign: 'center',
                          fontSize: '14px',
                          fontWeight: '600',
                          color: '#4a5568'
                        }}>
                          {entry.totalScore}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
          </div>
        )}

        {activeTab === 'tags' && (
          <div>
            <div style={{ 
              background: '#f7fafc', 
              padding: '16px', 
              borderRadius: '8px',
              marginBottom: '20px'
            }}>
              <h3 style={{ 
                fontSize: '15px', 
                fontWeight: '600', 
                color: '#2d3748',
                marginBottom: '8px'
              }}>
                🏷️ Group Tags
              </h3>
              <p style={{ fontSize: '13px', color: '#718096', marginBottom: '16px' }}>
                Select tags for this group. Questions in the Question Bank will be filtered to show only questions with these tags.
              </p>

              <div style={{ marginBottom: '16px' }}>
                <label style={{ 
                  display: 'block', 
                  fontSize: '13px', 
                  fontWeight: '600',
                  color: '#4a5568',
                  marginBottom: '8px'
                }}>
                  Select Tags
                </label>
                <Select
                  isMulti
                  value={selectedTags}
                  onChange={setSelectedTags}
                  options={availableTags}
                  placeholder="Choose tags for this group..."
                  menuPortalTarget={document.body}
                  styles={{
                    control: (base) => ({
                      ...base,
                      minHeight: '42px',
                      borderColor: '#e2e8f0',
                      '&:hover': { borderColor: '#cbd5e0' }
                    }),
                    multiValue: (base) => ({
                      ...base,
                      backgroundColor: '#eef2ff',
                      borderRadius: '4px'
                    }),
                    multiValueLabel: (base) => ({
                      ...base,
                      color: '#667eea',
                      fontWeight: '600',
                      fontSize: '13px'
                    }),
                    multiValueRemove: (base) => ({
                      ...base,
                      color: '#667eea',
                      ':hover': {
                        backgroundColor: '#667eea',
                        color: 'white',
                      },
                    }),
                    menuPortal: (base) => ({ ...base, zIndex: 1050 })
                  }}
                />
              </div>

              <button
                onClick={handleSaveTags}
                disabled={savingTags}
                className="btn btn-primary"
                style={{
                  fontSize: '14px',
                  padding: '10px 20px',
                  opacity: savingTags ? 0.7 : 1,
                  cursor: savingTags ? 'not-allowed' : 'pointer'
                }}
              >
                {savingTags ? 'Saving...' : 'Save Tags'}
              </button>
            </div>

            {group.tags && group.tags.length > 0 && (
              <div>
                <h3 style={{ 
                  fontSize: '15px', 
                  fontWeight: '600', 
                  color: '#2d3748',
                  marginBottom: '12px'
                }}>
                  Current Tags
                </h3>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                  {group.tags.map(tag => (
                    <span
                      key={tag._id}
                      style={{
                        padding: '6px 12px',
                        backgroundColor: '#eef2ff',
                        color: '#667eea',
                        borderRadius: '6px',
                        fontSize: '13px',
                        fontWeight: '600',
                        border: '1px solid #c7d2fe'
                      }}
                    >
                      🏷️ {tag.name}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {(!group.tags || group.tags.length === 0) && (
              <div style={{
                textAlign: 'center',
                padding: '40px',
                color: '#718096',
                backgroundColor: '#f7fafc',
                borderRadius: '8px'
              }}>
                <p style={{ fontSize: '18px', marginBottom: '8px' }}>🏷️</p>
                <p>No tags assigned to this group yet.</p>
                <p style={{ fontSize: '12px', marginTop: '4px' }}>
                  Assign tags to filter questions in the Question Bank.
                </p>
              </div>
            )}
          </div>
        )}

        {activeTab === 'categories' && (
          <div>
            <div style={{ 
              background: '#f7fafc', 
              padding: '16px', 
              borderRadius: '8px',
              marginBottom: '20px'
            }}>
              <h3 style={{ 
                fontSize: '15px', 
                fontWeight: '600', 
                color: '#2d3748',
                marginBottom: '8px'
              }}>
                📂 Group Categories
              </h3>
              <p style={{ fontSize: '13px', color: '#718096', marginBottom: '16px' }}>
                Select categories for this group. Flashcards will be filtered to show only flashcards with these categories.
              </p>

              <div style={{ marginBottom: '16px' }}>
                <label style={{ 
                  display: 'block', 
                  fontSize: '13px', 
                  fontWeight: '600',
                  color: '#4a5568',
                  marginBottom: '8px'
                }}>
                  Select Categories
                </label>
                <Select
                  isMulti
                  value={selectedCategories}
                  onChange={setSelectedCategories}
                  options={availableCategories}
                  placeholder="Choose categories for this group..."
                  menuPortalTarget={document.body}
                  styles={{
                    control: (base) => ({
                      ...base,
                      minHeight: '42px',
                      borderColor: '#e2e8f0',
                      '&:hover': { borderColor: '#cbd5e0' }
                    }),
                    multiValue: (base) => ({
                      ...base,
                      backgroundColor: '#eef2ff',
                      borderRadius: '4px'
                    }),
                    multiValueLabel: (base) => ({
                      ...base,
                      color: '#667eea',
                      fontWeight: '600',
                      fontSize: '13px'
                    }),
                    multiValueRemove: (base) => ({
                      ...base,
                      color: '#667eea',
                      ':hover': {
                        backgroundColor: '#667eea',
                        color: 'white',
                      },
                    }),
                    menuPortal: (base) => ({ ...base, zIndex: 1050 })
                  }}
                />
              </div>

              <button
                onClick={handleSaveCategories}
                disabled={savingCategories}
                className="btn btn-primary"
                style={{
                  fontSize: '14px',
                  padding: '10px 20px',
                  opacity: savingCategories ? 0.7 : 1,
                  cursor: savingCategories ? 'not-allowed' : 'pointer'
                }}
              >
                {savingCategories ? 'Saving...' : 'Save Categories'}
              </button>
            </div>

            {group.categories && group.categories.length > 0 && (
              <div>
                <h3 style={{ 
                  fontSize: '15px', 
                  fontWeight: '600', 
                  color: '#2d3748',
                  marginBottom: '12px'
                }}>
                  Current Categories
                </h3>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                  {group.categories.map(category => (
                    <span
                      key={category._id}
                      style={{
                        padding: '6px 12px',
                        backgroundColor: '#eef2ff',
                        color: '#667eea',
                        borderRadius: '6px',
                        fontSize: '13px',
                        fontWeight: '600',
                        border: '1px solid #c7d2fe'
                      }}
                    >
                      📂 {category.name}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {(!group.categories || group.categories.length === 0) && (
              <div style={{
                textAlign: 'center',
                padding: '40px',
                color: '#718096',
                backgroundColor: '#f7fafc',
                borderRadius: '8px'
              }}>
                <p style={{ fontSize: '18px', marginBottom: '8px' }}>📂</p>
                <p>No categories assigned to this group yet.</p>
                <p style={{ fontSize: '12px', marginTop: '4px' }}>
                  Assign categories to filter flashcards for this group.
                </p>
              </div>
            )}
          </div>
        )}

        {activeTab === 'fees' && (
          <div>
            {loadingFees ? (
              <div style={{ textAlign: 'center', padding: '40px', color: '#718096' }}>
                <p>Loading fee details...</p>
              </div>
            ) : feeStatistics ? (
              <>
                {/* Fee Statistics */}
                <div style={{ 
                  display: 'grid', 
                  gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                  gap: '16px',
                  marginBottom: '24px'
                }}>
                  <div style={{
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    padding: '20px',
                    borderRadius: '12px',
                    color: 'white'
                  }}>
                    <p style={{ fontSize: '13px', opacity: 0.9, marginBottom: '8px' }}>Total Students</p>
                    <p style={{ fontSize: '28px', fontWeight: '700' }}>{feeStatistics.totalStudents}</p>
                  </div>
                  
                  <div style={{
                    background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
                    padding: '20px',
                    borderRadius: '12px',
                    color: 'white'
                  }}>
                    <p style={{ fontSize: '13px', opacity: 0.9, marginBottom: '8px' }}>Students with Fees</p>
                    <p style={{ fontSize: '28px', fontWeight: '700' }}>{feeStatistics.studentsWithFees}</p>
                  </div>

                  <div style={{
                    background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
                    padding: '20px',
                    borderRadius: '12px',
                    color: 'white'
                  }}>
                    <p style={{ fontSize: '13px', opacity: 0.9, marginBottom: '8px' }}>Total Amount</p>
                    <p style={{ fontSize: '28px', fontWeight: '700' }}>₹{feeStatistics.totalAmount.toLocaleString()}</p>
                  </div>

                  <div style={{
                    background: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
                    padding: '20px',
                    borderRadius: '12px',
                    color: 'white'
                  }}>
                    <p style={{ fontSize: '13px', opacity: 0.9, marginBottom: '8px' }}>Amount Received</p>
                    <p style={{ fontSize: '28px', fontWeight: '700' }}>₹{feeStatistics.totalReceived.toLocaleString()}</p>
                  </div>

                  <div style={{
                    background: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
                    padding: '20px',
                    borderRadius: '12px',
                    color: 'white'
                  }}>
                    <p style={{ fontSize: '13px', opacity: 0.9, marginBottom: '8px' }}>Amount Pending</p>
                    <p style={{ fontSize: '28px', fontWeight: '700' }}>₹{feeStatistics.totalPending.toLocaleString()}</p>
                  </div>

                  <div style={{
                    background: 'linear-gradient(135deg, #30cfd0 0%, #330867 100%)',
                    padding: '20px',
                    borderRadius: '12px',
                    color: 'white'
                  }}>
                    <p style={{ fontSize: '13px', opacity: 0.9, marginBottom: '8px' }}>Collection Rate</p>
                    <p style={{ fontSize: '28px', fontWeight: '700' }}>{feeStatistics.percentageCollected}%</p>
                  </div>
                </div>

                {/* Payment Records */}
                <div style={{ marginTop: '32px' }}>
                  <h3 style={{ 
                    fontSize: '18px', 
                    fontWeight: '600', 
                    color: '#2d3748',
                    marginBottom: '16px'
                  }}>
                    💳 Payment Records ({payments.length})
                  </h3>
                  
                  {payments.length === 0 ? (
                    <div style={{
                      textAlign: 'center',
                      padding: '40px',
                      color: '#718096',
                      backgroundColor: '#f7fafc',
                      borderRadius: '8px'
                    }}>
                      <p style={{ fontSize: '18px', marginBottom: '8px' }}>💳</p>
                      <p>No payments recorded for this group yet.</p>
                    </div>
                  ) : (
                    <div style={{ 
                      background: 'white',
                      border: '1px solid #e2e8f0',
                      borderRadius: '8px',
                      overflow: 'hidden'
                    }}>
                      <div style={{ overflowX: 'auto' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                          <thead>
                            <tr style={{ backgroundColor: '#f7fafc' }}>
                              <th style={{ 
                                padding: '12px 16px', 
                                textAlign: 'left', 
                                fontSize: '12px',
                                fontWeight: '600',
                                color: '#4a5568',
                                borderBottom: '2px solid #e2e8f0'
                              }}>
                                Student
                              </th>
                              <th style={{ 
                                padding: '12px 16px', 
                                textAlign: 'left', 
                                fontSize: '12px',
                                fontWeight: '600',
                                color: '#4a5568',
                                borderBottom: '2px solid #e2e8f0'
                              }}>
                                Amount
                              </th>
                              <th style={{ 
                                padding: '12px 16px', 
                                textAlign: 'left', 
                                fontSize: '12px',
                                fontWeight: '600',
                                color: '#4a5568',
                                borderBottom: '2px solid #e2e8f0'
                              }}>
                                Date
                              </th>
                              <th style={{ 
                                padding: '12px 16px', 
                                textAlign: 'left', 
                                fontSize: '12px',
                                fontWeight: '600',
                                color: '#4a5568',
                                borderBottom: '2px solid #e2e8f0'
                              }}>
                                Method
                              </th>
                              <th style={{ 
                                padding: '12px 16px', 
                                textAlign: 'left', 
                                fontSize: '12px',
                                fontWeight: '600',
                                color: '#4a5568',
                                borderBottom: '2px solid #e2e8f0'
                              }}>
                                Transaction ID
                              </th>
                              <th style={{ 
                                padding: '12px 16px', 
                                textAlign: 'left', 
                                fontSize: '12px',
                                fontWeight: '600',
                                color: '#4a5568',
                                borderBottom: '2px solid #e2e8f0'
                              }}>
                                Recorded By
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {payments.map((payment, index) => (
                              <tr 
                                key={payment._id}
                                style={{ 
                                  backgroundColor: index % 2 === 0 ? 'white' : '#f9fafb',
                                  borderBottom: '1px solid #e2e8f0'
                                }}
                              >
                                <td style={{ padding: '12px 16px', fontSize: '13px', color: '#2d3748' }}>
                                  <div>
                                    <div style={{ fontWeight: '600' }}>
                                      {payment.feeManagement?.user?.name || payment.user?.name || 'N/A'}
                                    </div>
                                    <div style={{ fontSize: '12px', color: '#718096' }}>
                                      {payment.feeManagement?.user?.email || payment.user?.email || ''}
                                    </div>
                                  </div>
                                </td>
                                <td style={{ padding: '12px 16px', fontSize: '13px', fontWeight: '600', color: '#10b981' }}>
                                  ₹{payment.amount.toLocaleString()}
                                </td>
                                <td style={{ padding: '12px 16px', fontSize: '13px', color: '#2d3748' }}>
                                  {new Date(payment.paymentDate).toLocaleDateString('en-IN', {
                                    day: 'numeric',
                                    month: 'short',
                                    year: 'numeric'
                                  })}
                                </td>
                                <td style={{ padding: '12px 16px', fontSize: '13px', color: '#2d3748' }}>
                                  <span style={{
                                    padding: '4px 8px',
                                    backgroundColor: '#eef2ff',
                                    color: '#667eea',
                                    borderRadius: '4px',
                                    fontSize: '12px',
                                    fontWeight: '600'
                                  }}>
                                    {payment.paymentMethod}
                                  </span>
                                </td>
                                <td style={{ padding: '12px 16px', fontSize: '13px', color: '#718096', fontFamily: 'monospace' }}>
                                  {payment.transactionId || '-'}
                                </td>
                                <td style={{ padding: '12px 16px', fontSize: '13px', color: '#2d3748' }}>
                                  {payment.recordedBy?.name || 'N/A'}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </div>
              </>
            ) : (
              <div style={{
                textAlign: 'center',
                padding: '40px',
                color: '#718096',
                backgroundColor: '#f7fafc',
                borderRadius: '8px'
              }}>
                <p style={{ fontSize: '18px', marginBottom: '8px' }}>💰</p>
                <p>No fee management set up for this group yet.</p>
                <p style={{ fontSize: '12px', marginTop: '4px' }}>
                  Add students to fee management to track payments for this group.
                </p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default GroupDetails;
